'use client';

import React, { useState } from 'react';
import { Form } from '../components/form';
import { PageContainer } from '../components/page-container';
import { useSidebarCollapsed } from '../hooks/use-sidebar-collapsed';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  FileCheck,
  Layers,
  Clock,
  Stethoscope,
  Activity,
  Pill,
  HeartPulse,
  FileHeart,
  UserCheck,
  Calendar,
  FileSpreadsheet,
  Briefcase,
  Shield,
  UserCog,
  DollarSign,
  Receipt,
  PiggyBank,
  FileText,
  Cpu,
  Thermometer,
  Bell,
  Wifi,
  Video,
  CalendarClock,
  Film,
  AlertTriangle,
  Award,
  ScanFace,
  ScrollText,
  BookOpen,
  MessageSquare,
  BarChart3,
  FileBarChart,
  Scale,
  Building,
  Building2,
  Home,
  UsersRound,
  Plug,
  CreditCard,
  Settings,
  ChevronDown,
  ChevronRight,
  Menu,
  LogOut,
  BedDouble,
  DoorOpen,
  PieChart,
  Timer,
  Palette,
  Globe,
  ClipboardCheck,
  Moon,
  MapPin,
  CalendarRange,
  HomeIcon,
  Tag,
  FileSignature,
  Workflow,
  Zap,
  Import,
  Wrench,
  UtensilsCrossed,
  Webhook,
  Lock,
  MessageCircle,
  ShieldAlert,
  Landmark,
  Star,
  HelpCircle,
  LifeBuoy,
  CheckSquare,
  ListChecks,
  Bug,
  HardHat,
  AlertOctagon,
  FileWarning,
  GraduationCap,
  ArrowLeftRight,
  Banknote,
  RefreshCw,
  ShieldCheck,
  Search,
  Mail,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Logo } from '../components/logo';
import { Avatar } from '../components/avatar';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  /** Optional subcategory label — items with the same subcategory are grouped under a subtle heading */
  subcategory?: string;
  /** Optional role restriction — when set, item is only shown to users with one of these roles */
  roles?: string[];
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

type ActiveNavLocation = {
  groupTitle: string;
  href: string;
  subcategory?: string;
};

type NavBlock =
  | {
      kind: 'item';
      item: NavItem;
    }
  | {
      kind: 'subcategory';
      title: string;
      items: NavItem[];
    };

const RESIDENTIAL_DAILY_CARE_NAV_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'CLINICAL_LEAD',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'AGENCY_CONTRACTOR',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'CASE_MANAGER',
  'LIFESTYLE_OFFICER',
] as const;

const RESIDENTIAL_FINANCE_NAV_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_FINANCE_OFFICER',
  'FACILITY_MANAGER',
  'FINANCE_OFFICER',
] as const;

// ---------------------------------------------------------------------------
// Navigation IA (rewritten 2026-04-08)
// ---------------------------------------------------------------------------
// Design intent:
//   • Top of sidebar = "what I need to do today" (OVERVIEW + MY WORKSPACE)
//   • CARE DELIVERY consolidates what used to be 4 groups (Care Management,
//     Clinical, Monitoring, Telehealth) into workflow-ordered subcategories.
//   • QUALITY & RISK holds the operational compliance workflows (incidents,
//     complaints, improvement, WHS, IPC, safeguarding) — the stuff a DON
//     or quality officer sweeps daily.
//   • GOVERNANCE is separated out because it's strategic / board-level
//     (Responsible Persons, Registration, Prudential Standards, Risk Register,
//     Skills Matrix, Conflict of Interest). A Helen or Linda should be able
//     to tell at a glance which side of the house she's in.
//   • INSIGHTS merges the old DASHBOARDS and ANALYTICS groups (they were
//     entirely parallel), subcategorised by audience.
//   • Duplicates disambiguated by label (not by removing routes):
//       — "AN-ACC Register" → "AN-ACC Register (Care Minutes)" in RESIDENTIAL
//       — "Prudential Deposits" → "RAD/DAP Deposits" in FINANCE > Funding
//       — "Incidents (SIRS)" → "Serious Incidents (SIRS)"
//   • Every existing href is preserved — only labels/groups/subcategories change.
//   • The CI parent + 3 children are collapsed under a single "Continuous
//     Improvement" subcategory to stop the 4-item sprawl, but all 4 routes
//     remain reachable.
// ---------------------------------------------------------------------------

const sourceNavigation: NavGroup[] = [
  {
    title: 'OVERVIEW',
    items: [
      { label: 'Dashboard', href: '/overview', icon: LayoutDashboard },
      { label: 'My tasks', href: '/tasks', icon: CheckSquare },
      { label: 'All tasks', href: '/tasks/all', icon: ListChecks },
      { label: 'My profile', href: '/me/profile', icon: UserCog },
      // Command Centre — cross-functional leadership dashboards that don't
      // belong to any single module (moved out of the retired INSIGHTS group)
      {
        label: 'Executive view',
        href: '/dashboards/executive',
        icon: BarChart3,
        subcategory: 'Command Centre',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
      },
      {
        label: 'Multi-Facility View',
        href: '/dashboards/multi-facility',
        icon: Building,
        subcategory: 'Command Centre',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER'],
      },
      {
        label: 'Enterprise operations',
        href: '/dashboards/operations',
        icon: Activity,
        subcategory: 'Command Centre',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'FACILITY_MANAGER', 'CASE_MANAGER'],
      },
      {
        label: "Today's huddle",
        href: '/dashboards/huddle',
        icon: MessageSquare,
        subcategory: 'Command Centre',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'TEAM_LEADER',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
        ],
      },
      {
        label: 'Deadlines heatmap',
        href: '/dashboards/deadlines',
        icon: Clock,
        subcategory: 'Command Centre',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'FINANCE_OFFICER',
        ],
      },
      // Analytics & Reports — the cross-tenant reporting/benchmarking tools
      {
        label: 'Analytics Home',
        href: '/analytics',
        icon: BarChart3,
        subcategory: 'Analytics & Reports',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'QUALITY_OFFICER', 'FACILITY_MANAGER'],
      },
      {
        label: 'Reports Builder',
        href: '/analytics/reports',
        icon: FileBarChart,
        subcategory: 'Analytics & Reports',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'QUALITY_OFFICER', 'FACILITY_MANAGER'],
      },
      {
        label: 'Facility Comparison',
        href: '/analytics/facilities',
        icon: Building2,
        subcategory: 'Analytics & Reports',
      },
      {
        label: 'Workforce Reports',
        href: '/reports',
        icon: FileBarChart,
        subcategory: 'Analytics & Reports',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'QUALITY_OFFICER', 'FACILITY_MANAGER'],
      },
      {
        label: 'Scheduled Reports',
        href: '/reports/subscriptions',
        icon: FileBarChart,
        subcategory: 'Analytics & Reports',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'QUALITY_OFFICER', 'FACILITY_MANAGER'],
      },
      {
        label: 'Board pack',
        href: '/analytics/board-pack',
        icon: FileText,
        subcategory: 'Analytics & Reports',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
      },
      {
        label: 'Benchmarking',
        href: '/analytics/benchmarking',
        icon: Scale,
        subcategory: 'Analytics & Reports',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'QUALITY_OFFICER'],
      },
    ],
  },
  {
    title: 'MY WORKSPACE',
    items: [
      { label: 'My roster', href: '/me/roster', icon: Calendar },
      { label: 'My Timesheets', href: '/me/timesheets', icon: FileSpreadsheet },
      { label: 'My leave', href: '/me/leave', icon: Briefcase },
      { label: 'My Training', href: '/me/training', icon: GraduationCap },
      { label: 'My Courses', href: '/me/courses', icon: GraduationCap },
      { label: 'My Performance', href: '/me/performance', icon: UserCog },
      { label: 'My Policies', href: '/me/policies', icon: FileSpreadsheet },
      { label: 'My Onboarding', href: '/me/onboarding', icon: ClipboardCheck },
      { label: 'My Casual Conversion', href: '/me/casual-conversion', icon: ClipboardCheck },
    ],
  },
  {
    // CARE DELIVERY = prior CARE MANAGEMENT + CLINICAL + MONITORING + TELEHEALTH
    // Subcategorised by the care worker / nurse / coordinator workflow:
    //   At a Glance → Clients & Care → Clinical Care → Remote Monitoring → Telehealth
    title: 'CARE DELIVERY',
    items: [
      // At a Glance — contextual dashboards for the care team
      {
        label: 'Clinical Dashboard (DON)',
        href: '/dashboards/don-clinical',
        icon: Stethoscope,
        subcategory: 'At a Glance',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
        ],
      },
      {
        label: 'My Caseload',
        href: '/dashboards/caseload',
        icon: Users,
        subcategory: 'At a Glance',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'CASE_MANAGER',
          'ALLIED_HEALTH',
        ],
      },
      { label: 'Clients', href: '/clients', icon: Users, subcategory: 'Clients & Care' },
      {
        label: 'Care plans',
        href: '/care-plans',
        icon: ClipboardList,
        subcategory: 'Clients & Care',
      },
      {
        label: 'Assessments',
        href: '/assessments',
        icon: FileCheck,
        subcategory: 'Clients & Care',
      },
      {
        label: 'Care pathways',
        href: '/care-pathways',
        icon: HeartPulse,
        subcategory: 'Clients & Care',
      },
      {
        label: 'Programs',
        href: '/programs',
        icon: Layers,
        subcategory: 'Clients & Care',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
      },
      {
        label: 'Waitlist',
        href: '/waitlist',
        icon: Clock,
        subcategory: 'Clients & Care',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
      },
      {
        label: 'Observations',
        href: '/observations',
        icon: Activity,
        subcategory: 'Clinical Care',
      },
      {
        label: 'Clinical alerts',
        href: '/clinical/alerts',
        icon: Bell,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'FACILITY_MANAGER',
          'COORDINATOR',
          'TEAM_LEADER',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Clinical Handovers',
        href: '/clinical/handovers',
        icon: ClipboardList,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'FACILITY_MANAGER',
          'COORDINATOR',
          'TEAM_LEADER',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
          'CARE_WORKER',
          'ALLIED_HEALTH',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Overdue Assessments',
        href: '/clinical/overdue-assessments',
        icon: AlertTriangle,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'FACILITY_MANAGER',
          'COORDINATOR',
          'TEAM_LEADER',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Clinical Pathways',
        href: '/clinical/pathways',
        icon: Workflow,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'FACILITY_MANAGER',
          'COORDINATOR',
          'TEAM_LEADER',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Restorative Care',
        href: '/clinical/restorative',
        icon: HeartPulse,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'FACILITY_MANAGER',
          'COORDINATOR',
          'TEAM_LEADER',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'LIFESTYLE_OFFICER',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Clinical Supervision',
        href: '/people/development/supervision',
        icon: ClipboardCheck,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'FACILITY_MANAGER',
          'COORDINATOR',
          'TEAM_LEADER',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Medications',
        href: '/medications',
        icon: Pill,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
          'COORDINATOR',
          'CLINICAL_LEAD',
        ],
      },
      {
        label: 'MAR',
        href: '/medications/mar',
        icon: ClipboardCheck,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
          'CARE_WORKER',
          'CLINICAL_LEAD',
        ],
      },
      {
        label: 'S8 Register',
        href: '/medications/s8-register',
        icon: Lock,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
          'CLINICAL_LEAD',
        ],
      },
      {
        label: 'Wounds',
        href: '/wounds',
        icon: Stethoscope,
        subcategory: 'Clinical Care',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
        ],
      },
      {
        label: 'Advance care plans',
        href: '/advance-care-plans',
        icon: FileHeart,
        subcategory: 'Clinical Care',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'REGISTERED_NURSE', 'CLINICAL_LEAD'],
      },
      {
        label: 'Monitoring Overview',
        href: '/monitoring',
        icon: Activity,
        subcategory: 'Remote Monitoring',
      },
      {
        label: 'IoT Devices',
        href: '/monitoring/devices',
        icon: Cpu,
        subcategory: 'Remote Monitoring',
      },
      {
        label: 'Vital Signs',
        href: '/monitoring/vital-signs',
        icon: Thermometer,
        subcategory: 'Remote Monitoring',
      },
      {
        label: 'Alerts',
        href: '/monitoring/alerts',
        icon: Bell,
        subcategory: 'Remote Monitoring',
      },
      {
        label: 'AT-HM Equipment',
        href: '/monitoring/equipment',
        icon: Wifi,
        subcategory: 'Remote Monitoring',
      },
      {
        label: 'IoT Dashboard',
        href: '/dashboards/iot',
        icon: Cpu,
        subcategory: 'Remote Monitoring',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'REGISTERED_NURSE', 'CLINICAL_LEAD'],
      },
      {
        label: 'Telehealth sessions',
        href: '/telehealth',
        icon: Video,
        subcategory: 'Telehealth',
      },
      {
        label: 'Telehealth Schedule',
        href: '/telehealth/schedule',
        icon: CalendarClock,
        subcategory: 'Telehealth',
      },
      {
        label: 'Telehealth Recordings',
        href: '/telehealth/recordings',
        icon: Film,
        subcategory: 'Telehealth',
      },
    ],
  },
  {
    title: 'RESIDENTIAL',
    items: [
      // Residential Operations dashboard — contextual at-a-glance for the facility
      {
        label: 'Residential Operations',
        href: '/dashboards/facility-ops',
        icon: Building2,
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'TEAM_LEADER',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
        ],
      },
      { label: 'Rooms & Beds', href: '/residential/rooms', icon: BedDouble },
      { label: 'Admissions', href: '/residential/admissions', icon: DoorOpen },
      { label: 'Occupancy', href: '/residential/occupancy', icon: PieChart },
      { label: 'Care Minutes', href: '/residential/care-minutes', icon: Timer },
      { label: '24/7 RN Coverage', href: '/compliance/rn-coverage', icon: Stethoscope },
      { label: 'Daily Care Minutes', href: '/residential/care-minutes/daily', icon: Timer },
      // Relabelled to disambiguate from FINANCE > Funding > "AN-ACC Funding".
      // This is the care-minutes / classification lens; the $ lens lives in Finance.
      {
        label: 'AN-ACC Register (Care Minutes)',
        href: '/residential/care-minutes/an-acc',
        icon: Layers,
      },
      { label: 'Activities', href: '/residential/activities', icon: Palette },
      { label: 'Visitors', href: '/residential/visitors', icon: ClipboardCheck },
      { label: 'Night Rounds', href: '/residential/night-rounds', icon: Moon },
      {
        label: 'Rounds',
        href: '/residential/rounds',
        icon: ClipboardCheck,
        roles: [...RESIDENTIAL_DAILY_CARE_NAV_ROLES],
      },
      {
        label: 'ADL Events',
        href: '/residential/adl-events',
        icon: Activity,
        roles: [...RESIDENTIAL_DAILY_CARE_NAV_ROLES],
      },
      { label: 'Meal Management', href: '/residential/meal-management', icon: UtensilsCrossed },
      {
        label: 'DAP Accrual',
        href: '/residential/dap-accrual',
        icon: Banknote,
        roles: [...RESIDENTIAL_FINANCE_NAV_ROLES],
      },
    ],
  },
  {
    title: 'HOME CARE',
    items: [
      // Home Care Operations dashboard — contextual at-a-glance
      {
        label: 'Home Care Operations',
        href: '/dashboards/home-care-ops',
        icon: Home,
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
      },
      { label: 'Service Delivery', href: '/home-care/service-delivery', icon: MapPin },
      { label: 'Classifications', href: '/home-care/classifications', icon: Layers },
      { label: 'Service Areas', href: '/home-care/service-areas', icon: MapPin },
      { label: 'Schedules', href: '/home-care/schedules', icon: CalendarRange },
      { label: 'Home Assessments', href: '/home-care/assessments', icon: HomeIcon },
      { label: 'Monthly Statements', href: '/home-care/statements', icon: Mail },
      { label: 'Claims Export', href: '/home-care/claims', icon: FileText },
      { label: 'Exception Queue', href: '/home-care/exceptions', icon: AlertTriangle },
      { label: 'Reconciliation', href: '/home-care/reconciliation', icon: RefreshCw },
      { label: 'AT & Home Mods', href: '/home-care/at-hm', icon: Wrench },
    ],
  },
  {
    title: 'WORKFORCE',
    items: [
      // Workforce Dashboard — contextual at-a-glance
      {
        label: 'Workforce Dashboard',
        href: '/dashboards/workforce',
        icon: UserCheck,
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'TEAM_LEADER', 'FACILITY_MANAGER'],
      },
      {
        label: 'Staff',
        href: '/people/workers',
        icon: UserCheck,
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
      },
      {
        label: 'Training',
        href: '/people/learning',
        icon: GraduationCap,
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
      },
      {
        label: 'Worker Screening',
        href: '/people/compliance/screening',
        icon: ScanFace,
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
        ],
      },
      // Code of Conduct — workforce obligation under Aged Care Act 2024. The
      // "source of truth" page still lives in QUALITY & RISK > Safeguarding;
      // this is a convenience shortcut for workforce teams who need to push
      // acknowledgements and see breach investigations from the workforce lens.
      {
        label: 'Code of Conduct',
        href: '/people/policies-conduct/code',
        icon: Shield,
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Contractors',
        href: '/contractors',
        icon: UserCog,
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
      },
      {
        label: 'Contractor evidence queue',
        href: '/contractors/evidence/queue',
        icon: ClipboardCheck,
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'COMPLIANCE_OFFICER',
          'CARE_MANAGER',
        ],
      },
      {
        label: 'Contractor reconciliation',
        href: '/contractors/reconciliation',
        icon: FileSpreadsheet,
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'COMPLIANCE_OFFICER',
          'CARE_MANAGER',
        ],
      },
      {
        label: 'My contractor portal',
        href: '/me/contractor',
        icon: UserCog,
        roles: ['AGENCY_CONTRACTOR', 'ALLIED_HEALTH'],
      },
      // Phase 5 HR completeness — manager-side flows
      {
        label: 'Performance & Supervision',
        href: '/people/development/performance',
        icon: UserCog,
        subcategory: 'HR',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER', 'COORDINATOR', 'TEAM_LEADER'],
      },
      {
        label: 'PIPs',
        href: '/people/development/performance/pips',
        icon: ClipboardCheck,
        subcategory: 'HR',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER'],
      },
      {
        label: 'Clinical Supervision',
        href: '/people/development/supervision',
        icon: UserCog,
        subcategory: 'HR',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'FACILITY_MANAGER',
          'COORDINATOR',
          'CLINICAL_LEAD',
          'TEAM_LEADER',
        ],
      },
      {
        label: 'Training admin',
        href: '/people/learning',
        icon: GraduationCap,
        subcategory: 'HR',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER', 'COORDINATOR'],
      },
      {
        label: 'Onboarding admin',
        href: '/people/onboarding',
        icon: ClipboardCheck,
        subcategory: 'HR',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER', 'COORDINATOR'],
      },
      {
        label: 'Casual Conversion',
        href: '/people/development/casual-conversion',
        icon: ClipboardCheck,
        subcategory: 'HR',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER'],
      },
      {
        label: 'Workplace Policies',
        href: '/people/policies-conduct/policies',
        icon: FileSpreadsheet,
        subcategory: 'HR',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER', 'QUALITY_OFFICER'],
      },
      {
        label: 'Roster',
        href: '/roster',
        icon: Calendar,
        subcategory: 'Rostering',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
      },
      {
        label: 'Open Shifts',
        href: '/roster/open-shifts',
        icon: DoorOpen,
        subcategory: 'Rostering',
      },
      {
        label: 'Roster Periods',
        href: '/roster/periods',
        icon: CalendarRange,
        subcategory: 'Rostering',
      },
      {
        label: 'Roster Templates',
        href: '/roster/templates',
        icon: ClipboardList,
        subcategory: 'Rostering',
      },
      {
        label: 'Timesheets',
        href: '/roster/timesheets',
        icon: FileSpreadsheet,
        subcategory: 'Rostering',
      },
      { label: 'Leave', href: '/roster/leave', icon: Briefcase, subcategory: 'Rostering' },
      {
        label: 'Shift swaps',
        href: '/roster/shift-swaps',
        icon: ArrowLeftRight,
        subcategory: 'Rostering',
      },
      {
        label: 'Handovers',
        href: '/roster/handovers',
        icon: ClipboardList,
        subcategory: 'Rostering',
      },
      {
        label: 'Mobile Activity',
        href: '/workforce/mobile-activity',
        icon: Activity,
        subcategory: 'Operations',
      },
      {
        label: 'Handover Oversight',
        href: '/workforce/handovers',
        icon: ArrowLeftRight,
        subcategory: 'Operations',
      },
      {
        label: 'Pay Run Queue',
        href: '/payroll/queue',
        icon: Banknote,
        subcategory: 'Payroll',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER'],
      },
      {
        label: 'Timesheet Disputes',
        href: '/workforce/timesheets/disputes',
        icon: AlertTriangle,
        subcategory: 'Payroll',
      },
    ],
  },
  {
    // QUALITY & RISK = operational compliance ("what needs my attention today").
    // This is the Helen / Sarah / Linda sweep: incidents, complaints, quality,
    // improvement, safeguarding, workplace safety, infection control, audits.
    // Strategic board-level items (Responsible Persons, Registration, Prudential,
    // Skills Matrix, Conflict of Interest) live in GOVERNANCE instead.
    title: 'QUALITY & RISK',
    items: [
      // --- At a Glance — contextual dashboards for the compliance sweep ---
      {
        label: 'Compliance dashboard',
        href: '/dashboards/compliance',
        icon: Shield,
        subcategory: 'At a Glance',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'TEAM_LEADER',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Quality Dashboard',
        href: '/dashboards/quality',
        icon: Award,
        subcategory: 'At a Glance',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Risk Correlation',
        href: '/dashboards/risk-correlation',
        icon: AlertTriangle,
        subcategory: 'At a Glance',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
        ],
      },
      // --- Incidents & Complaints (daily sweep) ---
      {
        label: 'Serious Incidents (SIRS)',
        href: '/compliance/incidents',
        icon: AlertTriangle,
        subcategory: 'Incidents & Complaints',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'CARE_WORKER',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'CASE_MANAGER',
        ],
      },
      {
        label: 'Complaints',
        href: '/compliance/complaints',
        icon: MessageCircle,
        subcategory: 'Incidents & Complaints',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'CLINICAL_LEAD',
          'QUALITY_OFFICER',
          'REGISTERED_NURSE',
        ],
      },
      // --- Quality (standards, hub, feedback, star ratings) ---
      // All 4 live together so users stop asking "which Quality page do I want?".
      // "Quality Hub" is the workflow entry point; "Quality Standards" is the
      // strengthened-standards evidence view; "Star Ratings" is the public rating;
      // "Consumer Feedback" is the feedback loop tied to quality indicators.
      {
        label: 'Quality Hub',
        href: '/compliance/quality/hub',
        icon: Award,
        subcategory: 'Quality',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'CARE_WORKER',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'CASE_MANAGER',
        ],
      },
      {
        label: 'Quality Standards',
        href: '/compliance/quality',
        icon: Shield,
        subcategory: 'Quality',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Consumer Feedback',
        href: '/compliance/quality/feedback',
        icon: MessageSquare,
        subcategory: 'Quality',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'CARE_WORKER',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'CASE_MANAGER',
        ],
      },
      {
        label: 'Consumer Engagement',
        href: '/compliance/quality/engagement',
        icon: UsersRound,
        subcategory: 'Quality',
      },
      {
        label: 'QI Data Entry',
        href: '/compliance/quality/qi-data-entry',
        icon: ClipboardList,
        subcategory: 'Quality',
      },
      {
        label: 'Clinical Governance',
        href: '/compliance/clinical-governance',
        icon: ShieldCheck,
        subcategory: 'Quality',
      },
      {
        label: 'Star Ratings',
        href: '/compliance/star-ratings',
        icon: Star,
        subcategory: 'Quality',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'CLINICAL_LEAD', 'QUALITY_OFFICER'],
      },
      {
        label: 'Methodology',
        href: '/compliance/star-ratings/methodology',
        icon: Scale,
        subcategory: 'Quality',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'QUALITY_OFFICER',
        ],
      },
      // --- Continuous Improvement ---
      // All 4 CI routes kept, pulled together under one subcategory so the
      // "parent + 3 children" sprawl no longer eats the Compliance group.
      {
        label: 'Continuous Improvement',
        href: '/compliance/continuous-improvement',
        icon: RefreshCw,
        subcategory: 'Continuous Improvement',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'CARE_WORKER',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'CASE_MANAGER',
        ],
      },
      {
        label: 'Review',
        href: '/compliance/continuous-improvement/dashboard',
        icon: BarChart3,
        subcategory: 'Continuous Improvement',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'CLINICAL_LEAD', 'QUALITY_OFFICER'],
      },
      {
        label: 'New Improvement',
        href: '/compliance/continuous-improvement/new',
        icon: Zap,
        subcategory: 'Continuous Improvement',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'CLINICAL_LEAD', 'QUALITY_OFFICER'],
      },
      {
        label: 'My CI Actions',
        href: '/compliance/continuous-improvement/actions',
        icon: CheckSquare,
        subcategory: 'Continuous Improvement',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'CARE_WORKER',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'CASE_MANAGER',
        ],
      },
      // --- Safeguarding (restrictive practices, code of conduct, whistleblower) ---
      // Distinct workflow: protecting older people from harm/abuse.
      // Chapter 5 Div 2 of the Aged Care Act 2024.
      {
        label: 'Restrictive Practices',
        href: '/compliance/restrictive-practices',
        icon: Lock,
        subcategory: 'Safeguarding',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'CARE_WORKER',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'CASE_MANAGER',
        ],
      },
      {
        label: 'Code of Conduct',
        href: '/people/policies-conduct/code',
        icon: UserCheck,
        subcategory: 'Safeguarding',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'CARE_WORKER',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'CASE_MANAGER',
        ],
      },
      {
        label: 'Whistleblower',
        href: '/compliance/whistleblower',
        icon: ShieldAlert,
        subcategory: 'Safeguarding',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'REGISTERED_NURSE',
          'CLINICAL_LEAD',
          'ENROLLED_NURSE',
          'ALLIED_HEALTH',
          'CARE_WORKER',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'CASE_MANAGER',
        ],
      },
      // --- Workplace Safety (was HEALTH & SAFETY > Workplace Safety) ---
      {
        label: 'WHS Overview',
        href: '/compliance/whs',
        icon: HardHat,
        subcategory: 'Workplace Safety',
      },
      {
        label: 'WHS Incidents',
        href: '/compliance/whs/incidents',
        icon: AlertTriangle,
        subcategory: 'Workplace Safety',
      },
      {
        label: 'Hazard Register',
        href: '/compliance/whs/hazards',
        icon: AlertOctagon,
        subcategory: 'Workplace Safety',
      },
      {
        label: 'Chemical Register',
        href: '/compliance/whs/chemicals',
        icon: ShieldAlert,
        subcategory: 'Workplace Safety',
      },
      {
        label: 'Equipment',
        href: '/compliance/whs/equipment',
        icon: Wrench,
        subcategory: 'Workplace Safety',
      },
      {
        label: 'Psychosocial Hazards',
        href: '/compliance/whs/psychosocial',
        icon: HeartPulse,
        subcategory: 'Workplace Safety',
      },
      {
        label: 'Emergency Drills',
        href: '/compliance/whs/drills',
        icon: Bell,
        subcategory: 'Workplace Safety',
      },
      {
        label: 'Environmental',
        href: '/compliance/environmental',
        icon: Globe,
        subcategory: 'Workplace Safety',
      },
      // --- Infection Prevention & Control ---
      {
        label: 'Infection Prevention',
        href: '/compliance/ipc',
        icon: Bug,
        subcategory: 'Infection Control',
      },
      {
        label: 'Outbreaks',
        href: '/compliance/ipc/outbreaks',
        icon: AlertTriangle,
        subcategory: 'Infection Control',
      },
      {
        label: 'Surveillance',
        href: '/compliance/ipc/surveillance',
        icon: Activity,
        subcategory: 'Infection Control',
      },
      {
        label: 'IPC Reports',
        href: '/compliance/ipc/reports',
        icon: FileBarChart,
        subcategory: 'Infection Control',
      },
      // --- Audits & Reporting ---
      {
        label: 'Audit Program',
        href: '/compliance/audit-program',
        icon: ClipboardCheck,
        subcategory: 'Audits & Reporting',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'CLINICAL_LEAD', 'QUALITY_OFFICER'],
      },
      {
        label: 'Audit trail',
        href: '/compliance/audit-trail',
        icon: ScrollText,
        subcategory: 'Audits & Reporting',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'CLINICAL_LEAD', 'QUALITY_OFFICER'],
      },
      {
        label: 'Policies',
        href: '/people/policies-conduct/governance',
        icon: FileText,
        subcategory: 'Audits & Reporting',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'COORDINATOR',
          'CLINICAL_LEAD',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
        ],
      },
      {
        label: 'Compliance Reports',
        href: '/compliance/reporting',
        icon: FileCheck,
        subcategory: 'Audits & Reporting',
      },
      {
        label: 'SAH Evidence Oversight',
        href: '/compliance/sah-evidence',
        icon: ShieldCheck,
        subcategory: 'Audits & Reporting',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'REGION_MANAGER', 'REGION_COMPLIANCE_OFFICER'],
      },
      {
        label: 'Expiring Documents',
        href: '/compliance/documents/expiring',
        icon: FileWarning,
        subcategory: 'Audits & Reporting',
      },
      {
        label: 'Data Breaches',
        href: '/compliance/data-breach',
        icon: ShieldAlert,
        subcategory: 'Audits & Reporting',
      },
    ],
  },
  {
    // GOVERNANCE = strategic / board-level obligations.
    // Previously buried inside COMPLIANCE > Governance subcategory where it
    // competed with 20+ operational items. A board member or registered
    // provider Responsible Person should be able to land here in one click.
    title: 'GOVERNANCE',
    items: [
      {
        label: 'Governance Overview',
        href: '/compliance/governance',
        icon: BookOpen,
      },
      {
        label: 'Responsible Persons',
        href: '/compliance/responsible-persons',
        icon: Users,
        subcategory: 'Registered Provider',
      },
      {
        label: 'Registration',
        href: '/compliance/registration',
        icon: FileCheck,
        subcategory: 'Registered Provider',
      },
      {
        label: 'Prudential Standards',
        href: '/compliance/prudential',
        icon: Landmark,
        subcategory: 'Registered Provider',
      },
      {
        label: 'Risk Register',
        href: '/compliance/governance/risk-register',
        icon: AlertOctagon,
        subcategory: 'Governance Controls',
      },
      {
        label: 'Skills Matrix',
        href: '/compliance/governance/skills-matrix',
        icon: Award,
        subcategory: 'Governance Controls',
      },
      {
        label: 'Conflict of Interest',
        href: '/compliance/governance/conflicts',
        icon: Scale,
        subcategory: 'Governance Controls',
      },
    ],
  },
  {
    title: 'FINANCE',
    items: [
      {
        label: 'Finance Overview',
        href: '/finance',
        icon: DollarSign,
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER', 'COORDINATOR'],
      },
      // Finance Dashboard — contextual at-a-glance
      {
        label: 'Finance dashboard',
        href: '/dashboards/finance',
        icon: DollarSign,
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'],
      },
      { label: 'Invoices', href: '/finance/invoices', icon: DollarSign, subcategory: 'Billing' },
      { label: 'Claims', href: '/finance/claims', icon: Receipt, subcategory: 'Billing' },
      {
        label: 'Statements',
        href: '/finance/reports/statements',
        icon: FileText,
        subcategory: 'Billing',
      },
      {
        label: 'Aged Debtors',
        href: '/finance/reports/aged-debtors',
        icon: Clock,
        subcategory: 'Billing',
      },
      {
        label: 'Financial Reports',
        href: '/finance/reports',
        icon: FileBarChart,
        subcategory: 'Billing',
      },
      {
        label: 'Payment Reconciliation',
        href: '/finance/reports/reconciliation',
        icon: RefreshCw,
        subcategory: 'Billing',
      },
      { label: 'AN-ACC Funding', href: '/finance/an-acc', icon: Banknote, subcategory: 'Funding' },
      {
        label: 'Contributions',
        href: '/finance/contributions',
        icon: PiggyBank,
        subcategory: 'Funding',
      },
      {
        label: 'Budgets',
        href: '/finance/budgets/carry-over',
        icon: RefreshCw,
        subcategory: 'Funding',
      },
      // Relabelled to disambiguate from GOVERNANCE > "Prudential Standards".
      // This is the RAD/DAP deposit ledger; the standard itself lives in Governance.
      {
        label: 'RAD/DAP Deposits',
        href: '/compliance/prudential/deposits',
        icon: Landmark,
        subcategory: 'Funding',
      },
      {
        label: 'Agreements',
        href: '/pricing/agreements',
        icon: FileSignature,
        subcategory: 'Configuration',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'],
      },
      {
        label: 'Rate Cards',
        href: '/pricing/rate-cards',
        icon: Tag,
        subcategory: 'Configuration',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'],
      },
    ],
  },
  // NOTE: The former top-level MONITORING and TELEHEALTH groups have been
  // folded into CARE DELIVERY > Remote Monitoring / Telehealth subcategories
  // above. All hrefs are preserved — only the grouping changed.
  //
  // NOTE: The former INSIGHTS group (which itself had merged ANALYTICS +
  // DASHBOARDS) has been retired. Its 19 items were redistributed to the
  // module groups they logically belong to:
  //   • Executive / Multi-Facility / Enterprise Operations / Huddle /
  //     Deadlines / Analytics Home / Reports Builder / Board Pack /
  //     Benchmarking → OVERVIEW (Command Centre + Analytics & Reports subcats)
  //   • DON Clinical / Caseload → CARE DELIVERY > At a Glance
  //   • IoT → CARE DELIVERY > Remote Monitoring
  //   • Facility Ops ("Residential Operations") → RESIDENTIAL
  //   • Home Care Ops ("Home Care Operations") → HOME CARE
  //   • Workforce → WORKFORCE
  //   • Compliance / Quality / Risk Correlation → QUALITY & RISK > At a Glance
  //   • Finance → FINANCE
  // Goal: dashboards are now contextual to the module they belong to, not a
  // parallel tree. All /dashboards/* routes are preserved.
  {
    title: 'SUPPORT',
    items: [
      { label: 'Help Centre', href: '/help', icon: HelpCircle },
      { label: 'Support Tickets', href: '/help/tickets', icon: LifeBuoy },
    ],
  },
  {
    title: 'TOOLS',
    items: [
      { label: 'Activity Log', href: '/activity', icon: Activity },
      { label: 'Translate', href: '/translate', icon: Globe },
    ],
  },
  {
    title: 'SETTINGS',
    items: [
      {
        label: 'Settings Home',
        href: '/settings',
        icon: Settings,
        subcategory: 'Organisation',
      },
      {
        label: 'Organisation',
        href: '/settings/organisation',
        icon: Building2,
        subcategory: 'Organisation',
      },
      {
        label: 'Facilities',
        href: '/settings/facilities',
        icon: Building2,
        subcategory: 'Organisation',
      },
      {
        label: 'Facility Config',
        href: '/settings/facility-config',
        icon: Wrench,
        subcategory: 'Organisation',
      },
      { label: 'Modules', href: '/settings/modules', icon: Layers, subcategory: 'Organisation' },
      { label: 'Portal', href: '/settings/portal', icon: Globe, subcategory: 'Organisation' },
      { label: 'Users & Roles', href: '/settings/users', icon: UsersRound, subcategory: 'People' },
      { label: 'Approvals', href: '/settings/approvals', icon: Workflow, subcategory: 'People' },
      {
        label: 'Notifications',
        href: '/settings/notifications',
        icon: Bell,
        subcategory: 'Notifications',
      },
      {
        label: 'Notification Preferences',
        href: '/settings/notification-preferences',
        icon: Bell,
        subcategory: 'Notifications',
      },
      {
        label: 'Workflows',
        href: '/settings/workflows',
        icon: Workflow,
        subcategory: 'Automation',
      },
      {
        label: 'Retention',
        href: '/settings/retention',
        icon: ClipboardList,
        subcategory: 'Automation',
      },
      {
        label: 'Public Holidays',
        href: '/settings/holidays',
        icon: Calendar,
        subcategory: 'Automation',
      },
      { label: 'Data Import', href: '/settings/import', icon: Import, subcategory: 'Integrations' },
      {
        label: 'Integrations',
        href: '/settings/integrations',
        icon: Plug,
        subcategory: 'Integrations',
      },
      // SSO removed — will be ported as Next.js server action when Enterprise tier launches
      {
        label: 'Webhook API Keys',
        href: '/settings/webhooks',
        icon: Webhook,
        subcategory: 'Integrations',
      },
      {
        label: 'Billing Config',
        href: '/settings/billing',
        icon: CreditCard,
        subcategory: 'System',
      },
      {
        label: 'EBA Agreements',
        href: '/settings/payroll/eba',
        icon: Briefcase,
        subcategory: 'System',
      },
      { label: 'System', href: '/settings/system', icon: Settings, subcategory: 'System' },
      {
        label: 'Organisation configuration',
        href: '/admin/system/tenant-config',
        icon: FileText,
        subcategory: 'System',
        roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
      },
      {
        label: 'AI Usage',
        href: '/admin/system/ai-usage',
        icon: Cpu,
        subcategory: 'System',
        roles: ['SYSTEM_ADMIN'],
      },
      { label: 'Security', href: '/settings/security', icon: ShieldCheck, subcategory: 'System' },
    ],
  },
];

const DOMAIN_GROUPS = [
  'Clinical',
  'Residential',
  'Home Care',
  'Workforce',
  'Compliance',
  'Finance',
  'HR',
  'Reports & Analytics',
  'Admin',
  'Me',
] as const;

type DomainGroupTitle = (typeof DOMAIN_GROUPS)[number];

function domainForDashboardShortcut(sourceGroupTitle: string): DomainGroupTitle {
  switch (sourceGroupTitle) {
    case 'CARE DELIVERY':
      return 'Clinical';
    case 'RESIDENTIAL':
      return 'Residential';
    case 'HOME CARE':
      return 'Home Care';
    case 'WORKFORCE':
      return 'Workforce';
    case 'QUALITY & RISK':
    case 'GOVERNANCE':
      return 'Compliance';
    case 'FINANCE':
      return 'Finance';
    default:
      return 'Reports & Analytics';
  }
}

function domainForItem(item: NavItem, sourceGroupTitle: string): DomainGroupTitle {
  const href = item.href;

  if (href === '/dashboards' || href.startsWith('/dashboards/')) {
    return domainForDashboardShortcut(sourceGroupTitle);
  }

  if (
    href === '/overview' ||
    href === '/tasks' ||
    href.startsWith('/tasks/') ||
    href.startsWith('/me/') ||
    href === '/help' ||
    href.startsWith('/help/') ||
    href === '/activity' ||
    href === '/translate'
  ) {
    return 'Me';
  }

  if (href === '/settings' || href.startsWith('/settings/') || href.startsWith('/admin/system')) {
    return 'Admin';
  }

  if (
    href === '/analytics' ||
    href.startsWith('/analytics/') ||
    href === '/reports' ||
    href.startsWith('/reports/')
  ) {
    return 'Reports & Analytics';
  }

  if (href === '/people' || href.startsWith('/people/')) {
    return 'HR';
  }

  if (href.startsWith('/residential/') || href === '/compliance/rn-coverage') {
    return 'Residential';
  }

  if (href.startsWith('/home-care/')) {
    return 'Home Care';
  }

  if (
    href === '/roster' ||
    href.startsWith('/roster/') ||
    href.startsWith('/workforce/') ||
    href.startsWith('/contractors') ||
    href.startsWith('/payroll/')
  ) {
    return 'Workforce';
  }

  if (
    href === '/finance' ||
    href.startsWith('/finance/') ||
    href.startsWith('/pricing/') ||
    href === '/compliance/prudential' ||
    href.startsWith('/compliance/prudential/')
  ) {
    return 'Finance';
  }

  if (
    href.startsWith('/clinical') ||
    href.startsWith('/clients') ||
    href.startsWith('/care-plans') ||
    href.startsWith('/assessments') ||
    href === '/care-pathways' ||
    href === '/programs' ||
    href === '/waitlist' ||
    href.startsWith('/observations') ||
    href.startsWith('/medications') ||
    href.startsWith('/wounds') ||
    href.startsWith('/advance-care-plans') ||
    href.startsWith('/monitoring') ||
    href.startsWith('/telehealth')
  ) {
    return 'Clinical';
  }

  if (href.startsWith('/compliance')) {
    return 'Compliance';
  }

  return 'Me';
}

function subcategoryForItem(
  item: NavItem,
  sourceGroupTitle: string,
  domainTitle: DomainGroupTitle,
): string | undefined {
  const href = item.href;

  switch (domainTitle) {
    case 'Clinical':
      if (href.startsWith('/monitoring') || href === '/dashboards/iot') return 'Remote Monitoring';
      if (href.startsWith('/telehealth')) return 'Telehealth';
      if (
        href.startsWith('/clinical') ||
        href.startsWith('/observations') ||
        href.startsWith('/medications') ||
        href.startsWith('/wounds') ||
        href.startsWith('/advance-care-plans')
      ) {
        return 'Clinical Care';
      }
      if (href.startsWith('/dashboards')) return 'At a Glance';
      return 'Clients & Care';
    case 'Residential':
      if (href.startsWith('/dashboards')) return 'At a Glance';
      if (href.includes('care-minutes') || href === '/compliance/rn-coverage')
        return 'Care Minutes';
      if (href.includes('activities') || href.includes('visitors') || href.includes('meal')) {
        return 'Lifestyle & Hospitality';
      }
      return 'Operations';
    case 'Home Care':
      if (href.startsWith('/dashboards')) return 'At a Glance';
      if (href.includes('claims') || href.includes('statements') || href.includes('exceptions')) {
        return 'SAH Finance';
      }
      if (href.includes('reconciliation')) return 'Reconciliation';
      if (href.includes('at-hm')) return 'AT & Home Mods';
      if (href.includes('assessments')) return 'Assessments';
      return 'Service Delivery';
    case 'Workforce':
      if (href.startsWith('/dashboards')) return 'At a Glance';
      if (href.startsWith('/contractors')) return 'Contractors';
      if (href.startsWith('/payroll')) return 'Payroll';
      if (href.startsWith('/workforce')) return 'Operations';
      if (href === '/availability') return 'Availability';
      return 'Rostering';
    case 'Compliance':
      if (href.startsWith('/dashboards')) return 'At a Glance';
      if (href.includes('/incidents') || href.includes('/complaints'))
        return 'Incidents & Complaints';
      if (
        href.includes('/quality') ||
        href.includes('/star-ratings') ||
        href.includes('/clinical-governance')
      ) {
        return 'Quality';
      }
      if (href.includes('/continuous-improvement')) return 'Continuous Improvement';
      if (
        href.includes('/restrictive-practices') ||
        href.includes('/code-of-conduct') ||
        href.includes('/whistleblower')
      ) {
        return 'Safeguarding';
      }
      if (href.includes('/whs') || href.includes('/environmental')) return 'Workplace Safety';
      if (href.includes('/ipc')) return 'Infection Control';
      if (
        href.includes('/governance') ||
        href.includes('/responsible-persons') ||
        href.includes('/registration')
      ) {
        return 'Provider Governance';
      }
      if (href.includes('/data-breach')) return 'Privacy';
      return 'Evidence & Reporting';
    case 'Finance':
      if (href.startsWith('/dashboards')) return 'At a Glance';
      if (href.includes('/reports') || href.includes('/invoices') || href.includes('/claims'))
        return 'Billing';
      if (
        href.includes('/an-acc') ||
        href.includes('/contributions') ||
        href.includes('/budgets') ||
        href.includes('/prudential')
      ) {
        return 'Funding';
      }
      if (href.startsWith('/pricing')) return 'Configuration';
      return 'Overview';
    case 'HR':
      if (href.startsWith('/people/workers')) return 'Staff';
      if (href.includes('/training')) return 'Training';
      if (href.includes('/onboarding')) return 'Onboarding';
      if (href.includes('/offboarding')) return 'Offboarding';
      if (href.includes('/performance') || href.includes('/clinical-supervision'))
        return 'Performance';
      if (href.includes('/casual-conversion')) return 'Employment Conditions';
      if (href.startsWith('/people/compliance')) return 'Screening & Compliance';
      if (href.includes('/policies')) return 'Policies';
      return 'HR Operations';
    case 'Reports & Analytics':
      if (href.startsWith('/dashboards'))
        return sourceGroupTitle === 'OVERVIEW' ? 'Command Centre' : 'Dashboards';
      if (href.startsWith('/analytics')) return 'Analytics';
      return 'Reports';
    case 'Admin':
      if (href.startsWith('/admin/system')) return 'Platform';
      if (
        href.includes('/integrations') ||
        href.includes('/webhooks') ||
        href.includes('/import')
      ) {
        return 'Integrations';
      }
      if (href.includes('/notifications') || href.includes('/escalations')) return 'Notifications';
      if (href.includes('/users') || href.includes('/approvals')) return 'People';
      return 'Organisation settings';
    case 'Me':
      if (href.startsWith('/me/contractor')) return 'Contractor Portal';
      if (href.startsWith('/me/')) return 'My Records';
      if (href.startsWith('/help')) return 'Support';
      if (href === '/activity' || href === '/translate') return 'Utilities';
      return 'Daily Work';
    default:
      return item.subcategory;
  }
}

function buildDomainNavigation(groups: NavGroup[]): NavGroup[] {
  const buckets = new Map<DomainGroupTitle, NavItem[]>(
    DOMAIN_GROUPS.map((groupTitle) => [groupTitle, []]),
  );

  for (const group of groups) {
    for (const item of group.items) {
      const domainTitle = domainForItem(item, group.title);
      const subcategory = subcategoryForItem(item, group.title, domainTitle);
      buckets.get(domainTitle)?.push({
        ...item,
        subcategory,
      });
    }
  }

  return DOMAIN_GROUPS.map((groupTitle) => ({
    title: groupTitle,
    items: buckets.get(groupTitle) ?? [],
  })).filter((group) => group.items.length > 0);
}

const navigation: NavGroup[] = buildDomainNavigation(sourceNavigation);

const ALL_APP_NAV_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
  'COORDINATOR',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'CLINICAL_LEAD',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'CARE_WORKER',
  'FINANCE_OFFICER',
  'AGENCY_CONTRACTOR',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'CASE_MANAGER',
  'LIFESTYLE_OFFICER',
];

const ROLE_NAV_ACCESS: Record<string, string[]> = {
  Clinical: [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'AGENCY_CONTRACTOR',
    'CASE_MANAGER',
    'LIFESTYLE_OFFICER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ],
  Residential: [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'FACILITY_MANAGER',
    'LIFESTYLE_OFFICER',
    'QUALITY_OFFICER',
  ],
  'Home Care': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_FINANCE_OFFICER',
    'COORDINATOR',
    'TEAM_LEADER',
    'CLINICAL_LEAD',
    'ALLIED_HEALTH',
    'REGISTERED_NURSE',
    'CASE_MANAGER',
    'FINANCE_OFFICER',
  ],
  Workforce: [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_FINANCE_OFFICER',
    'COORDINATOR',
    'TEAM_LEADER',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'CARE_WORKER',
    'REGISTERED_NURSE',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'AGENCY_CONTRACTOR',
    'FINANCE_OFFICER',
  ],
  Compliance: [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'TEAM_LEADER',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'CASE_MANAGER',
  ],
  Finance: [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_FINANCE_OFFICER',
    'FINANCE_OFFICER',
    'COORDINATOR',
    'FACILITY_MANAGER',
  ],
  HR: [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'TEAM_LEADER',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ],
  'Reports & Analytics': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'REGION_FINANCE_OFFICER',
    'COORDINATOR',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'FINANCE_OFFICER',
    'CASE_MANAGER',
  ],
  Admin: ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
  Me: ALL_APP_NAV_ROLES,
};

function formatNavSectionLabel(label: string) {
  return label
    .toLowerCase()
    .replace(/\b[a-z]/g, (char) => char.toUpperCase())
    .replace(/\bAnd\b/g, '&');
}

export type DashboardLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string;
};

export interface DashboardSidebarAction {
  label: string;
  icon: React.ElementType;
  onClick: () => void;
}

export interface DashboardLayoutProps {
  children: React.ReactNode;
  currentPath?: string;
  user?: {
    name: string;
    role: string;
    avatarUrl?: string;
    initials: string;
  };
  signOutHref?: string;
  pageTitle?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  actions?: React.ReactNode;
  stats?: React.ReactNode;
  enabledModules?: string[] | null;
  /** When true, settings nav items are also filtered by enabledModules (for restricted plans like CI-free) */
  filterSettingsNav?: boolean;
  /** Optional facility switcher rendered below the logo in the sidebar */
  facilitySwitcher?: React.ReactNode;
  /** Optional compact facility switcher rendered in the header when the desktop sidebar is collapsed */
  headerFacilitySwitcher?: React.ReactNode;
  /** Raw role string for role-based navigation filtering */
  userRole?: string;
  /** Final app-level route access check. Lets app RBAC/module guards be the sidebar source of truth. */
  canAccessPath?: (href: string) => boolean;
  /** Optional content rendered in the top-right of the header (e.g. notification bell) */
  headerRight?: React.ReactNode;
  /** Optional app actions rendered above the sidebar user section. */
  sidebarActions?: DashboardSidebarAction[];
  /** When true, only show personal work and tenant setup navigation groups (onboarding mode) */
  isOnboarding?: boolean;
  /** Optional app-router link component for client-side navigation. Falls back to a plain anchor. */
  LinkComponent?: React.ElementType<DashboardLinkProps>;
}

// Keep the sidebar short by default: Me remains open, and the active
// work area is opened on demand. Subcategories are independently collapsible.
const DEFAULT_EXPANDED_GROUPS = new Set(['Me']);

const SIDEBAR_GROUP_STORAGE_KEY = 'statura-sidebar-collapsed-v3';
const SIDEBAR_SUBCATEGORY_STORAGE_KEY = 'statura-sidebar-subcategories-collapsed-v1';
const SIDEBAR_SCROLL_STORAGE_KEY = 'statura-sidebar-scroll-v2';
const LEGACY_SIDEBAR_SCROLL_STORAGE_KEY = 'statura-sidebar-scroll';
const DESKTOP_SIDEBAR_STORAGE_KEY = 'statura-desktop-sidebar-collapsed-v1';

function getDefaultCollapsedGroups(groups: NavGroup[]): Set<string> {
  return new Set(
    groups.map((group) => group.title).filter((title) => !DEFAULT_EXPANDED_GROUPS.has(title)),
  );
}

function getSubcategoryKey(groupTitle: string, subcategory: string): string {
  return `${groupTitle}::${subcategory}`;
}

function getDefaultCollapsedSubcategories(groups: NavGroup[]): Set<string> {
  const collapsed = new Set<string>();
  for (const group of groups) {
    for (const item of group.items) {
      if (item.subcategory) {
        collapsed.add(getSubcategoryKey(group.title, item.subcategory));
      }
    }
  }
  return collapsed;
}

function parseStoredSet(value: string | null): Set<string> | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((item): item is string => typeof item === 'string'));
  } catch {
    return null;
  }
}

function getActiveNavLocation(currentPath: string, groups: NavGroup[]): ActiveNavLocation | null {
  let bestMatch: ActiveNavLocation | null = null;

  for (const group of groups) {
    for (const item of group.items) {
      const isMatch = currentPath === item.href || currentPath.startsWith(`${item.href}/`);
      if (!isMatch) continue;

      if (!bestMatch || item.href.length > bestMatch.href.length) {
        bestMatch = {
          groupTitle: group.title,
          href: item.href,
          subcategory: item.subcategory,
        };
      }
    }
  }

  return bestMatch;
}

function loadCollapsedGroups(currentPath: string, groups: NavGroup[]): Set<string> {
  let collapsed = getDefaultCollapsedGroups(groups);

  if (typeof window !== 'undefined') {
    const stored = parseStoredSet(localStorage.getItem(SIDEBAR_GROUP_STORAGE_KEY));
    if (stored) {
      collapsed = stored;
    }
  }

  const activeLocation = getActiveNavLocation(currentPath, groups);
  if (activeLocation) {
    collapsed.delete(activeLocation.groupTitle);
  }

  return collapsed;
}

function loadCollapsedSubcategories(currentPath: string, groups: NavGroup[]): Set<string> {
  let collapsed = getDefaultCollapsedSubcategories(groups);

  if (typeof window !== 'undefined') {
    const stored = parseStoredSet(localStorage.getItem(SIDEBAR_SUBCATEGORY_STORAGE_KEY));
    if (stored) {
      collapsed = stored;
    }
  }

  const activeLocation = getActiveNavLocation(currentPath, groups);
  if (activeLocation?.subcategory) {
    collapsed.delete(getSubcategoryKey(activeLocation.groupTitle, activeLocation.subcategory));
  }

  return collapsed;
}

function buildNavBlocks(items: NavItem[]): NavBlock[] {
  const blocks: NavBlock[] = [];

  for (const item of items) {
    if (!item.subcategory) {
      blocks.push({ kind: 'item', item });
      continue;
    }

    const previousBlock = blocks[blocks.length - 1];
    if (previousBlock?.kind === 'subcategory' && previousBlock.title === item.subcategory) {
      previousBlock.items.push(item);
    } else {
      blocks.push({ kind: 'subcategory', title: item.subcategory, items: [item] });
    }
  }

  return blocks;
}

export function DashboardLayout({
  children,
  currentPath = '/',
  user = { name: 'Admin User', role: 'Coordinator', initials: 'AU' },
  signOutHref = '/auth/signout',
  pageTitle,
  breadcrumbs,
  actions,
  stats,
  enabledModules,
  filterSettingsNav,
  facilitySwitcher,
  headerFacilitySwitcher,
  userRole,
  canAccessPath,
  headerRight,
  sidebarActions,
  isOnboarding,
  LinkComponent = 'a',
}: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPreference, setDesktopSidebarCollapsed] = useState<boolean | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem(DESKTOP_SIDEBAR_STORAGE_KEY);
      return stored === 'true' ? true : stored === 'false' ? false : null;
    } catch {
      return null;
    }
  });
  const desktopSidebarCollapsed = useSidebarCollapsed(sidebarPreference);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() =>
    loadCollapsedGroups(currentPath, navigation),
  );
  const [collapsedSubcategories, setCollapsedSubcategories] = useState<Set<string>>(() =>
    loadCollapsedSubcategories(currentPath, navigation),
  );
  const navRef = React.useRef<HTMLElement>(null);
  const scrollSaveFrameRef = React.useRef<number | null>(null);
  const activeNavLocation = React.useMemo(
    () => getActiveNavLocation(currentPath, navigation),
    [currentPath],
  );

  const LinkElement = LinkComponent;

  // Filter navigation based on enabled modules (item-level filtering)
  // If enabledModules is null/undefined, show everything (backwards compatible)
  // Each item is individually checked against enabledModules.
  // Groups with no matching items are removed entirely.
  // Me stays available for every plan; Admin remains available unless the
  // caller explicitly asks settings to be module-filtered.
  const ALWAYS_VISIBLE_GROUPS = new Set(filterSettingsNav ? ['Me'] : ['Admin', 'Me']);
  const NAV_LANDING_PATHS = new Set([
    '/clinical',
    '/compliance',
    '/home-care',
    '/residential',
    '/dashboards',
  ]);
  let filteredNavigation = enabledModules
    ? navigation
        .map((group) => {
          if (ALWAYS_VISIBLE_GROUPS.has(group.title)) return group;
          return {
            ...group,
            items: group.items.filter(
              (item) =>
                enabledModules.includes(item.href) ||
                (NAV_LANDING_PATHS.has(item.href) &&
                  enabledModules.some((mod) => mod.startsWith(item.href + '/'))),
            ),
          };
        })
        .filter((group) => ALWAYS_VISIBLE_GROUPS.has(group.title) || group.items.length > 0)
    : navigation;

  // Apply onboarding filter — only show essential nav groups during setup
  if (isOnboarding) {
    const ONBOARDING_GROUPS = new Set(['Admin', 'Me']);
    filteredNavigation = filteredNavigation.filter((group) => ONBOARDING_GROUPS.has(group.title));
  }

  // In the web app, canAccessPath is the route-level source of truth. Keep
  // the legacy sidebar role allowlists only as a fallback for consumers that
  // render this package without an app-level access callback.
  if (userRole && !canAccessPath) {
    filteredNavigation = filteredNavigation
      .filter((group) =>
        group.items.some((item) =>
          (item.roles ?? ROLE_NAV_ACCESS[group.title] ?? []).includes(userRole),
        ),
      )
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          (item.roles ?? ROLE_NAV_ACCESS[group.title] ?? []).includes(userRole),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }

  if (canAccessPath) {
    filteredNavigation = filteredNavigation
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => canAccessPath(item.href)),
      }))
      .filter((group) => group.items.length > 0);
  }

  const toggleGroup = (title: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      // Persist to localStorage
      try {
        localStorage.setItem(SIDEBAR_GROUP_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  };

  const toggleSubcategory = (groupTitle: string, subcategory: string) => {
    const key = getSubcategoryKey(groupTitle, subcategory);
    setCollapsedSubcategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      try {
        localStorage.setItem(SIDEBAR_SUBCATEGORY_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  };

  const toggleDesktopSidebar = () => {
    setDesktopSidebarCollapsed(() => {
      const next = !desktopSidebarCollapsed;
      try {
        localStorage.setItem(DESKTOP_SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  };

  const saveSidebarScroll = React.useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const scrollTop = String(nav.scrollTop);
    try {
      localStorage.setItem(SIDEBAR_SCROLL_STORAGE_KEY, scrollTop);
    } catch {
      // Ignore storage errors
    }
    try {
      sessionStorage.setItem(SIDEBAR_SCROLL_STORAGE_KEY, scrollTop);
    } catch {
      // Ignore storage errors
    }
  }, []);

  const restoreSidebarScroll = React.useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;

    let savedScroll: string | null = null;
    try {
      savedScroll =
        localStorage.getItem(SIDEBAR_SCROLL_STORAGE_KEY) ??
        sessionStorage.getItem(SIDEBAR_SCROLL_STORAGE_KEY) ??
        sessionStorage.getItem(LEGACY_SIDEBAR_SCROLL_STORAGE_KEY);
    } catch {
      savedScroll = null;
    }

    if (!savedScroll) return;
    const nextScrollTop = Number(savedScroll);
    if (!Number.isFinite(nextScrollTop)) return;

    const applyScroll = () => {
      const maxScrollTop = Math.max(0, nav.scrollHeight - nav.clientHeight);
      nav.scrollTop = Math.min(nextScrollTop, maxScrollTop);
    };

    applyScroll();
    window.requestAnimationFrame(() => {
      applyScroll();
      window.requestAnimationFrame(applyScroll);
    });
  }, []);

  React.useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    restoreSidebarScroll();

    const onScroll = () => {
      if (scrollSaveFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollSaveFrameRef.current);
      }
      scrollSaveFrameRef.current = window.requestAnimationFrame(() => {
        saveSidebarScroll();
        scrollSaveFrameRef.current = null;
      });
    };

    const onPageHide = () => saveSidebarScroll();

    nav.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onPageHide);
    return () => {
      nav.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onPageHide);
      if (scrollSaveFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollSaveFrameRef.current);
      }
    };
  }, [restoreSidebarScroll, saveSidebarScroll]);

  // Auto-expand the active work area and its active subcategory after navigation.
  React.useEffect(() => {
    if (!activeNavLocation) {
      restoreSidebarScroll();
      return;
    }

    setCollapsedGroups((prev) => {
      if (!prev.has(activeNavLocation.groupTitle)) return prev;
      const next = new Set(prev);
      next.delete(activeNavLocation.groupTitle);
      try {
        localStorage.setItem(SIDEBAR_GROUP_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        // Ignore storage errors
      }
      return next;
    });

    if (activeNavLocation.subcategory) {
      setCollapsedSubcategories((prev) => {
        const key = getSubcategoryKey(activeNavLocation.groupTitle, activeNavLocation.subcategory!);
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        try {
          localStorage.setItem(SIDEBAR_SUBCATEGORY_STORAGE_KEY, JSON.stringify([...next]));
        } catch {
          // Ignore storage errors
        }
        return next;
      });
    }

    restoreSidebarScroll();
  }, [activeNavLocation, restoreSidebarScroll]);

  const renderNavItem = (item: NavItem, nested = false) => {
    const isActive = activeNavLocation?.href === item.href;
    const Icon = item.icon;

    return (
      <LinkElement
        key={item.href}
        href={item.href}
        onClick={saveSidebarScroll}
        className={cn(
          'flex min-h-8 items-center gap-2.5 rounded-lg px-2.5 py-1.5 font-body text-[13px] font-medium transition-interactive duration-200',
          nested && 'pl-2',
          isActive
            ? 'bg-white/8 text-white shadow-[inset_0_0_0_1px_rgba(150,169,152,0.22)]'
            : 'text-slate-200 hover:bg-white/5 hover:text-white',
        )}
      >
        <Icon
          className={cn(
            'h-4 w-4 shrink-0',
            isActive ? 'text-statura-green-on-dark' : 'text-slate-300',
          )}
        />
        <span className="truncate">{item.label}</span>
      </LinkElement>
    );
  };

  return (
    <div className="flex h-screen min-w-0 overflow-hidden bg-surface-shell">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-ink-dark/55 backdrop-blur-[2px] lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-print-chrome="true"
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-white/8 bg-statura-ink-dark transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full max-lg:invisible',
          desktopSidebarCollapsed ? 'lg:hidden' : 'lg:static lg:translate-x-0',
        )}
      >
        {/* Logo — links to dashboard */}
        <LinkElement
          href="/overview"
          aria-label="Go to overview"
          className="flex h-16 items-center border-b border-white/6 px-6 transition-opacity hover:opacity-80"
        >
          <Logo variant="full-dark" width={140} height={36} />
        </LinkElement>

        {/* Facility switcher (multi-facility tenants) */}
        {facilitySwitcher}

        {/* Navigation */}
        <nav
          ref={navRef}
          aria-label="Primary navigation"
          onClickCapture={saveSidebarScroll}
          className="flex-1 overscroll-contain overflow-y-auto px-2.5 py-2"
        >
          {filteredNavigation.map((group) => {
            const groupCollapsed = collapsedGroups.has(group.title);
            const groupIsActive = activeNavLocation?.groupTitle === group.title;
            const groupPanelId = `sidebar-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

            return (
              <div key={group.title} className="mb-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.title)}
                  aria-expanded={!groupCollapsed}
                  aria-controls={groupPanelId}
                  className={cn(
                    'sticky top-0 z-10 flex min-h-8 w-full items-center justify-between rounded-lg bg-statura-ink-dark/95 px-2.5 py-1.5 font-body text-[11px] font-semibold text-slate-300 backdrop-blur transition-interactive duration-200 hover:bg-white/5 hover:text-white',
                    groupIsActive && 'text-white',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate">{formatNavSectionLabel(group.title)}</span>
                    <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-slate-300">
                      {group.items.length}
                    </span>
                  </span>
                  {groupCollapsed ? (
                    <ChevronRight className="h-3 w-3 shrink-0" />
                  ) : (
                    <ChevronDown className="h-3 w-3 shrink-0" />
                  )}
                </button>
                {!groupCollapsed && (
                  <div id={groupPanelId} className="space-y-0.5 pb-1 pt-1">
                    {buildNavBlocks(group.items).map((block, blockIndex) => {
                      if (block.kind === 'item') {
                        return renderNavItem(block.item);
                      }

                      const subcategoryKey = getSubcategoryKey(group.title, block.title);
                      const subcategoryCollapsed = collapsedSubcategories.has(subcategoryKey);
                      const subcategoryIsActive = block.items.some(
                        (item) => item.href === activeNavLocation?.href,
                      );
                      const subcategoryPanelId = `${groupPanelId}-${block.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${blockIndex}`;

                      return (
                        <div key={`${subcategoryKey}-${blockIndex}`} className="pt-0.5">
                          <button
                            type="button"
                            onClick={() => toggleSubcategory(group.title, block.title)}
                            aria-expanded={!subcategoryCollapsed}
                            aria-controls={subcategoryPanelId}
                            className={cn(
                              'flex min-h-7 w-full items-center justify-between rounded-md px-2.5 py-1 font-body text-[11px] font-semibold text-slate-300 transition-interactive duration-200 hover:bg-white/5 hover:text-white',
                              subcategoryIsActive && 'text-statura-green-on-dark',
                            )}
                          >
                            <span className="truncate">{block.title}</span>
                            <span className="ml-2 flex shrink-0 items-center gap-1.5">
                              <span className="text-[10px] font-medium text-slate-300">
                                {block.items.length}
                              </span>
                              {subcategoryCollapsed ? (
                                <ChevronRight className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )}
                            </span>
                          </button>
                          {!subcategoryCollapsed && (
                            <div
                              id={subcategoryPanelId}
                              className="ml-2.5 space-y-0.5 border-l border-white/6 pl-1.5"
                            >
                              {block.items.map((item) => renderNavItem(item, true))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {sidebarActions && sidebarActions.length > 0 && (
          <div className="border-t border-slate-700/50 px-3 py-3">
            <div className="space-y-1">
              {sidebarActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.label}
                    type="button"
                    onClick={() => {
                      saveSidebarScroll();
                      setSidebarOpen(false);
                      action.onClick();
                    }}
                    className="flex min-h-9 w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left font-body text-[13px] font-medium text-slate-300 transition-interactive duration-200 hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green"
                  >
                    <Icon
                      className="h-4 w-4 shrink-0 text-statura-green-on-dark"
                      aria-hidden="true"
                    />
                    <span className="truncate">{action.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* User section */}
        <div className="border-t border-slate-700/50 p-4">
          <div className="flex items-center gap-3">
            <Avatar src={user.avatarUrl} fallback={user.initials} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium text-white">{user.name}</p>
              <p className="truncate text-xs text-slate-300">{formatNavSectionLabel(user.role)}</p>
            </div>
            <Form action={signOutHref} method="POST">
              <button
                type="submit"
                className="flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 text-slate-300 transition-interactive duration-200 hover:bg-white/5 hover:text-white"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </Form>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden">
        {/* Top bar — breadcrumbs + search + notifications in one row */}
        <header
          className="flex h-14 min-w-0 items-center gap-4 border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur lg:px-6"
          data-print-chrome="true"
        >
          <button
            type="button"
            onClick={toggleDesktopSidebar}
            aria-label={desktopSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={desktopSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden rounded-lg p-2 text-slate-500 transition-interactive duration-200 hover:bg-slate-100 hover:text-statura-ink lg:inline-flex"
          >
            <Menu className="h-5 w-5" />
          </button>
          {desktopSidebarCollapsed && headerFacilitySwitcher && (
            <div className="hidden min-w-0 lg:block">{headerFacilitySwitcher}</div>
          )}
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
            className="flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 text-slate-500 transition-interactive duration-200 hover:bg-slate-100 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Logo variant="full" width={100} height={28} className="lg:hidden" />
          {/* Breadcrumbs inline in the header */}
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav className="hidden min-w-0 items-center gap-1.5 text-sm text-slate-500 lg:flex">
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <span className="text-slate-300">/</span>}
                  {idx < breadcrumbs.length - 1 && crumb.href ? (
                    <LinkElement
                      href={crumb.href}
                      className="min-w-0 truncate transition-interactive hover:text-statura-green-accessible"
                    >
                      {crumb.label}
                    </LinkElement>
                  ) : (
                    <span className="min-w-0 truncate font-medium text-slate-700">
                      {crumb.label}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}
          <div className="flex-1" />
          <button
            onClick={() =>
              document.dispatchEvent(
                new KeyboardEvent('keydown', {
                  key: 'k',
                  metaKey: true,
                  bubbles: true,
                }),
              )
            }
            className="hidden lg:flex items-center gap-2 rounded-xl border border-slate-200 bg-surface-soft/90 px-3 py-2 font-body text-xs text-text-muted transition-interactive duration-200 hover:border-statura-green-accessible/20 hover:bg-surface-soft"
          >
            <Search className="w-3.5 h-3.5" />
            <span>Search...</span>
            <kbd className="ml-auto text-[10px] bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono">
              ⌘K
            </kbd>
          </button>
          {headerRight}
        </header>

        {/* Page header — only rendered when there's a title, actions, or stats */}
        {(pageTitle || actions || stats) && (
          <div className="border-b border-slate-200/80 bg-white/85 px-4 py-4 backdrop-blur sm:px-6 lg:px-6">
            <div className="flex min-w-0 flex-col items-start gap-3 sm:flex-row sm:justify-between">
              {pageTitle && (
                <h2 className="min-w-0 max-w-4xl break-words font-heading text-page-title font-bold leading-tight text-text-heading [text-wrap:balance]">
                  {pageTitle}
                </h2>
              )}
              {actions && (
                <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:max-w-[44rem] sm:justify-end [&>*]:min-w-0">
                  {actions}
                </div>
              )}
            </div>
            {stats && <div className="mt-3 flex flex-wrap gap-3">{stats}</div>}
          </div>
        )}

        {/* Content */}
        <main id="main-content" className="min-w-0 flex-1 overflow-y-auto bg-surface-page">
          <PageContainer>{children}</PageContainer>
        </main>
      </div>
    </div>
  );
}
